import { Link } from 'react-router-dom';
import { formatCredits, formatUsd, PAGE } from '@kioskgaming/ui';
import ShopCreditPurchaseTrigger from '../credits/ShopCreditPurchaseTrigger';

type Tone = 'blue' | 'emerald';

type Props = {
  creditBalance: number;
  creditAvailable: number;
  usdBalanceNet: number;
  isLoading?: boolean;
  costRate: number;
  defaultOpenCreditPurchase?: boolean;
  onBalancesRefresh?: () => void | Promise<void>;
  onOrdered?: () => void;
  tone?: Tone;
  showHeading?: boolean;
};

const headingClass: Record<Tone, string> = {
  blue: 'text-blue-900/70',
  emerald: 'text-stone-500',
};

const cardClass: Record<Tone, string> = {
  blue: 'border-blue-100 bg-white',
  emerald: 'border-stone-200 bg-white',
};

const valueClass: Record<Tone, string> = {
  blue: 'text-blue-950',
  emerald: 'text-stone-900',
};

const linkClass: Record<Tone, string> = {
  blue: 'text-blue-700 hover:text-blue-900',
  emerald: 'text-emerald-700 hover:text-emerald-900',
};

export default function ShopWalletCard({
  creditBalance,
  creditAvailable,
  usdBalanceNet,
  isLoading = false,
  costRate,
  defaultOpenCreditPurchase,
  onBalancesRefresh,
  onOrdered,
  tone = 'blue',
  showHeading = true,
}: Props) {
  return (
    <section className="space-y-2">
      {showHeading ? (
        <h2 className={`text-xs font-semibold uppercase tracking-wide ${headingClass[tone]}`}>Wallet</h2>
      ) : null}
      <div
        className={`flex flex-col gap-3 rounded-2xl border p-4 sm:flex-row sm:items-center sm:justify-between ${cardClass[tone]}`}
      >
        <div className="grid grid-cols-2 gap-6 sm:grid-cols-3">
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-wide text-stone-500">Credits</p>
            <p className={`text-lg font-semibold tabular-nums ${valueClass[tone]}`}>
              {isLoading ? '…' : formatCredits(creditBalance)}
            </p>
            {!isLoading && Math.abs(creditAvailable - creditBalance) > 0.009 ? (
              <p className="text-xs text-stone-500">Available {formatCredits(creditAvailable)}</p>
            ) : null}
            <Link to="/transactions" className={`text-xs font-semibold ${linkClass[tone]}`}>
              {PAGE.creditTransactions}
            </Link>
          </div>
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-wide text-stone-500">USD wallet</p>
            <p className={`text-lg font-semibold tabular-nums ${valueClass[tone]}`}>
              {isLoading ? '…' : formatUsd(usdBalanceNet)}
            </p>
            <Link to="/online-transactions" className={`text-xs font-semibold ${linkClass[tone]}`}>
              {PAGE.usdWalletTransactions}
            </Link>
          </div>
        </div>
        <ShopCreditPurchaseTrigger
          className="w-full sm:w-auto sm:min-w-[14rem]"
          costRate={costRate}
          creditBalance={creditBalance}
          creditBalanceLoading={isLoading}
          onlineBalanceNet={usdBalanceNet}
          onlineBalanceLoading={isLoading}
          conversionCostRate={costRate}
          defaultOpen={defaultOpenCreditPurchase}
          // Only the debt banner's "Buy credits now" opens this card's dialog, so land on Buy credits.
          defaultOpenTab="balance"
          tone={tone}
          onBalancesRefresh={onBalancesRefresh}
          onOrdered={onOrdered}
        />
      </div>
    </section>
  );
}
